import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactPlayer, { ReactPlayerProps } from 'react-player';

export const VideoPlayer = React.forwardRef<
  ReactPlayer,
  { url: string } & Pick<
    ReactPlayerProps,
    'onProgress' | 'onPlay' | 'onPause' | 'onEnded'
  >
>(({ url, onProgress, onPlay, onPause, onEnded }, forwardRef) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | string>('100%');
  useLayoutEffect(() => {
    if (ref.current) {
      setWidth(ref.current.getBoundingClientRect().width);
    }
  }, []);
  return (
    <div ref={ref}>
      <ReactPlayer
        ref={forwardRef}
        url={url || 'PREVIEW'}
        light={true}
        controls={true}
        width={width}
        height={
          typeof width === 'number' ? Math.round(width / (16 / 9)) : undefined
        }
        onProgress={onProgress}
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        config={{
          vimeo: {
            playerOptions: {
              autoplay: true
            }
          },
          youtube: {
            playerVars: {
              autoplay: 1
            }
          }
        }}
      />
    </div>
  );
});

const getTimeRemainingThreshold = (totalDurationInSeconds: number) => {
  if (totalDurationInSeconds < 60 * 5) {
    return 20;
  }
  if (totalDurationInSeconds < 60 * 10) {
    return 40;
  }
  if (totalDurationInSeconds < 60 * 20) {
    return 60;
  }
  return 120;
};

export const VideoPlayerWithCompletionTracking = ({
  url,
  onComplete,
  onPlayPause
}: {
  url: string;
  onComplete: () => void;
  onPlayPause?: (isPlaying: boolean) => void;
}) => {
  const [playing, setPlaying] = useState(false);
  const player = useRef<ReactPlayer>(null);
  const counter = useRef(0);
  const completed = useRef(false);
  const reachedEnd = useRef(false);
  useEffect(() => {
    counter.current = 0;
    completed.current = false;
  }, [url]);

  useEffect(() => {
    if (!playing) {
      return;
    }
    const interval = setInterval(() => {
      counter.current += 1;
    }, 1000);
    return () => clearInterval(interval);
  }, [playing]);

  useEffect(() => {
    onPlayPause?.(playing);
  }, [playing, onPlayPause]);

  return (
    <VideoPlayer
      ref={player}
      url={url}
      onPlay={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      onEnded={() => {
        reachedEnd.current = true;
        setPlaying(false);
      }}
      onProgress={({ playedSeconds, played }) => {
        if (completed.current) {
          return;
        }
        const duration = player.current?.getDuration() || Infinity;
        const timeRemaining = Math.round(duration - playedSeconds);
        const percentPlayed = counter.current / duration;
        const timeRemainingThreshold = getTimeRemainingThreshold(duration);
        if (
          (!playing || played === 1 || reachedEnd.current) &&
          timeRemaining < timeRemainingThreshold &&
          percentPlayed > 0.2
        ) {
          completed.current = true;
          onComplete();
        }
      }}
    />
  );
};
